import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  Alert,
  Grid,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  EventAvailable as EventIcon
} from '@mui/icons-material';

const AddToCalendarModal = ({ event, open, onClose }) => {
  const [selectedOption, setSelectedOption] = useState('download');
  const [email, setEmail] = useState('');

  // Helper function to normalize date to string format (YYYY-MM-DD)
  const normalizeDateString = (dateInput) => {
    if (!dateInput) return '';
    
    if (dateInput instanceof Date) {
      // Convert Date object to YYYY-MM-DD string
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (typeof dateInput === 'string') {
      return dateInput;
    }
    
    return '';
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleAddToCalendar = () => {
    // Handle the selected option
    switch (selectedOption) {
      case 'download':
        downloadCalendarFile();
        break;
      case 'google':
        addToGoogleCalendar();
        break;
      case 'outlook':
        addToOutlookCalendar();
        break;
      case 'apple':
        addToAppleCalendar();
        break;
      default:
        break;
    }
  };

  const downloadCalendarFile = () => {
    // Create ICS file content
    const icsContent = createICSContent();

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onClose();
  };

  const createICSContent = () => {
    // Format date to YYYYMMDDTHHMMSSZ format
    const formatDate = (dateString, timeString) => {
      const date = new Date(dateString);
      const [hours, minutes] = timeString.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };

    const dateStr = normalizeDateString(event.date);
    const start = formatDate(dateStr, event.startTime);
    const end = formatDate(dateStr, event.endTime);

    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Your Company//Event Calendar//EN
BEGIN:VEVENT
UID:${Date.now()}@yourapp.com
DTSTAMP:${formatDate(new Date(), '12:00')}
DTSTART:${start}
DTEND:${end}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;
  };

  const addToGoogleCalendar = () => {
    const dateStr = normalizeDateString(event.date);
    const startDate = new Date(`${dateStr}T${event.startTime}`);
    const endDate = new Date(`${dateStr}T${event.endTime}`);

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${startDate.toISOString().replace(/-|:|\.\d+/g, '')}/${endDate.toISOString().replace(/-|:|\.\d+/g, '')}`,
      details: event.description,
      location: event.location
    });

    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
    onClose();
  };

  const addToOutlookCalendar = () => {
    const dateStr = normalizeDateString(event.date);
    const startDate = new Date(`${dateStr}T${event.startTime}`);
    const endDate = new Date(`${dateStr}T${event.endTime}`);

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      subject: event.title,
      body: event.description,
      location: event.location
    });

    window.open(`https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`, '_blank');
    onClose();
  };

  const addToAppleCalendar = () => {
    // Apple Calendar doesn't have a direct web API, so we'll download the ICS file
    downloadCalendarFile();
  };



  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            Add to Calendar
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {event?.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body2">
              {(() => {
                // Handle both string and Date object formats
                let dateObj;
                if (event?.date instanceof Date) {
                  dateObj = event.date;
                } else if (typeof event?.date === 'string') {
                  // Parse date components to avoid UTC conversion issues
                  const [year, month, day] = event.date.split('-').map(Number);
                  dateObj = new Date(year, month - 1, day); // month is 0-indexed
                } else {
                  return 'Date not available';
                }
                return dateObj.toLocaleDateString();
              })()} • {event?.startTime} - {event?.endTime}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EventIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body2">{event?.locationType === 'remote' ? 'Online Event' : event?.location}</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Select how you'd like to add this event:</FormLabel>
          <RadioGroup
            name="calendar-options"
            value={selectedOption}
            onChange={handleOptionChange}
          >
            <FormControlLabel
              value="download"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DownloadIcon sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body1">Download .ICS file</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add to any calendar application
                    </Typography>
                  </Box>
                </Box>
              }
            />

            <FormControlLabel
              value="google"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 24, height: 24, mr: 1, background: 'linear-gradient(45deg, #4285F4 33%, #34A853 33%, #34A853 66%, #EA4335 66%)', borderRadius: '50%' }} />
                  <Box>
                    <Typography variant="body1">Add to Google Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opens in a new window
                    </Typography>
                  </Box>
                </Box>
              }
            />

            <FormControlLabel
              value="outlook"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 24, height: 24, mr: 1, backgroundColor: '#0078D4', borderRadius: '50%' }} />
                  <Box>
                    <Typography variant="body1">Add to Outlook Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opens in a new window
                    </Typography>
                  </Box>
                </Box>
              }
            />

            <FormControlLabel
              value="apple"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 24, height: 24, mr: 1, backgroundColor: '#000', borderRadius: '50%' }} />
                  <Box>
                    <Typography variant="body1">Add to Apple Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Downloads .ICS file
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {selectedOption === 'email' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
            />
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Adding this event to your calendar helps you remember and get reminders.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleAddToCalendar}
          variant="contained"
          disabled={selectedOption === 'email' && !email}
          startIcon={<CalendarIcon />}
        >
          Add to Calendar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCalendarModal;